"use client";

import { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { api, useApi } from "@/lib/useApi";
import { Icon } from "../icons";
import { Button, ErrorBox, Notice, Segmented } from "../ui";
import { useDialog } from "@/components/Dialog";
import { VOTE_ACCESS, type VoteAccess } from "@/domain/votes/vote";
import { Select } from "../Select";

type Role = "admin" | "user";
interface UsersResp {
  users: { id: string; name: string; email: string; role: Role; voteAccess: VoteAccess; createdAt: number; lastSeenAt: number | null; confirmed: boolean }[];
  selfId: string;
}

const box = "h-6 rounded-md border border-line bg-surface-2 px-1.5 text-[12px]";
const EMPTY = { name: "", email: "", password: "", role: "user" as Role };

/**
 * The registered users (administrators only): each one's role and access to the vote (vote, view only, off — an
 * administrator always votes), switchable, deleting a user with all their data, and adding a user (also while
 * registration is closed).
 */
export function UsersPanel() {
  const { t, f } = useI18n();
  const dialog = useDialog();
  const { data, reload } = useApi<UsersResp>("/api/users");
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const set = (patch: Partial<typeof EMPTY>) => setForm((x) => ({ ...x, ...patch }));
  const roles: { value: Role; label: string }[] = [
    { value: "admin", label: t("set.account.roleAdmin") },
    { value: "user", label: t("set.account.roleUser") },
  ];

  // one request, then the list again; true when it went through
  const change = async (path: string, init: Parameters<typeof api>[1]) => {
    setError(null);
    setAdded(null);
    try {
      await api(path, init);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      reload();
    }
  };
  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(await change("/api/users", { method: "POST", json: form }))) return;
    setAdded(form.name);
    setForm(EMPTY);
  };
  const remove = async (u: UsersResp["users"][number]) => (await dialog.confirm(t("set.users.deleteConfirm", { name: u.name, email: u.email }), { danger: true })) && void change(`/api/users/${u.id}`, { method: "DELETE" });

  if (!data) return <div className="skeleton h-40" />;
  return (
    <section>
      <p className="truncate py-0.5 text-[11.5px] text-muted" title={t("set.users.hint")}>{t("set.users.hint")}</p>
      {error && <div className="pb-2"><ErrorBox>{error}</ErrorBox></div>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] border-t border-line text-[12.5px]">
          <thead className="text-left text-[11px] text-muted">
            <tr>
              <th className="px-3 py-1.5 font-medium">{t("set.users.name")}</th>
              <th className="px-3 py-1.5 font-medium">{t("set.users.email")}</th>
              <th className="px-3 py-1.5 font-medium">{t("set.users.role")}</th>
              <th className="px-3 py-1.5 font-medium" title={t("set.users.voteHelp")}>{t("set.users.vote")}</th>
              <th className="px-3 py-1.5 font-medium">{t("set.users.since")}</th>
              <th className="px-3 py-1.5 font-medium">{t("set.users.lastSeen")}</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-line border-t border-line">
            {data.users.map((u) => {
              const self = u.id === data.selfId;
              return (
                <tr key={u.id}>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span className="font-medium text-ink">{u.name}</span>
                    {self && <span className="ml-1.5 text-[11px] text-muted">({t("set.users.you")})</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-ink-2">
                    {u.email}
                    {!u.confirmed && <span className="ml-1.5 text-[11px] text-warn-ink" title={t("set.users.unconfirmedHint")}>{t("set.users.unconfirmed")}</span>}
                  </td>
                  <td className="px-3 py-1.5" aria-label={t("set.users.roleOf", { name: u.name })}>
                    {self ? <span className="text-ink-2">{roles.find((r) => r.value === u.role)?.label}</span> : <Segmented<Role> size="xs" value={u.role} options={roles} onChange={(role) => void change(`/api/users/${u.id}`, { method: "PATCH", json: { role } })} />}
                  </td>
                  <td className="px-3 py-1.5">
                    {u.role === "admin" ? (
                      <span className="text-ink-2" title={t("set.users.voteAdmin")}>{t("set.users.voteAccess.vote")}</span>
                    ) : (
                      <Select<VoteAccess> label={t("set.users.voteOf", { name: u.name })} value={u.voteAccess} options={VOTE_ACCESS.map((a) => ({ value: a, label: t(`set.users.voteAccess.${a}`) }))} onChange={(voteAccess) => void change(`/api/users/${u.id}`, { method: "PATCH", json: { voteAccess } })} className="w-[130px]" />
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-muted tabular">{f.date(u.createdAt)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-muted tabular">{u.lastSeenAt === null ? "—" : f.dateTimeLocal(u.lastSeenAt)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {!self && (
                      <button type="button" onClick={() => remove(u)} aria-label={t("set.users.delete", { name: u.name })} title={t("set.users.delete", { name: u.name })} className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg text-muted hover:bg-bad-soft hover:text-bad-ink">
                        <Icon name="trash" className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <form onSubmit={add} aria-label={t("set.users.add")} className="flex flex-wrap items-center gap-1.5 border-t border-line py-2">
        <input aria-label={t("set.users.name")} placeholder={t("auth.name")} className={`${box} w-[150px]`} required minLength={2} maxLength={40} value={form.name} onChange={(e) => set({ name: e.target.value })} />
        <input aria-label={t("auth.email")} placeholder={t("auth.email")} className={`${box} w-[200px]`} type="email" required value={form.email} onChange={(e) => set({ email: e.target.value })} />
        <input aria-label={t("auth.password")} placeholder={t("set.users.password")} className={`${box} w-[190px]`} type="password" autoComplete="new-password" required minLength={10} value={form.password} onChange={(e) => set({ password: e.target.value })} />
        <Segmented<Role> size="xs" value={form.role} options={roles} onChange={(role) => set({ role })} />
        <Button size="xs" variant="primary">{t("set.users.add")}</Button>
      </form>
      {added && <div className="pb-2"><Notice tone="good">✓ {t("set.users.added", { name: added })}</Notice></div>}
    </section>
  );
}
